import React, { useState, useEffect } from 'react'
import InformationSection from "../InformationSection"
import classes from '../Sections.module.scss'
import { useForm, SubmitHandler, useFieldArray } from 'react-hook-form'
import Button from "../../../shared/ComponentLibrary/RButton"
import useOptions from '../../../../customHooks/useOptions'
import useProfile from '../../../../customHooks/useProfile'
import Project from './project'

interface IDataToSubmit {
  projects: ProjectData[]
  noPriorExperience: boolean
}

const Projects = ({ profileData, setProfileData, setDirtyForm }: ProfileTabsProps) => {
  const projectsData: UserProject[] = profileData.projects
  const { getPracticeAreaList, getIndustryList, getSoftwareProductList, getResponsibilityList, getLanguageList, getMethodologyList } = useOptions()

  const [practiceAreaListData, setPracticeAreaListData] = useState<PracticeAreaList>({})
  const [industryListData, setIndustryListData] = useState<IndustryList>({})
  const [softwareProductListData, setSoftwareProductListData] = useState<SoftwareProductList>({})
  const [responsibilityListData, setResponsibilityListData] = useState<ResponsibilityList>({})
  const [methodologiesListData, setMethodologiesListData] = useState<MethodologyList>({})
  const [languageListData, setLanguageListData] = useState<LanguageList>({})

  const { register, handleSubmit, reset, control, formState: { isDirty } } = useForm({
    defaultValues: {
      projects: [...profileData.projects],
      noPriorExperience: profileData.noPriorExperience
    }
  })
  const { fields, append, remove } = useFieldArray({ control, name: "projects" })

  useEffect(() => { reset({ projects: [...projectsData] }) }, [projectsData])

  const [allProjectsChangedState, setAllProjectsChangedState] = useState<boolean[]>([])
  const [allProjectsArbitraryKeys, setAllProjectsArbitraryKeys] = useState<number[]>([])

  function projectHasChanged(key: number) {
    return allProjectsChangedState[key];
  }

  function setProjectHasChanged(key: number, newState: boolean) {
    let newStates = allProjectsChangedState.map((state, index) => {
      if (index === key) {
        return newState;
      }
      return state;
    });
    setAllProjectsChangedState(newStates);
  }

  function setAllProjectsToOneChangedState(newState: boolean) {
    let newStates = allProjectsChangedState.map(() => newState);
    setAllProjectsChangedState(newStates);
  }

  function addNewProjectChangedState(newState: boolean) {
    let newStates = [...allProjectsChangedState, newState];
    setAllProjectsChangedState(newStates);
    let newKey = new Uint32Array(1);
    crypto.getRandomValues(newKey);
    let newKeys = [...allProjectsArbitraryKeys, newKey[0]];
    setAllProjectsArbitraryKeys(newKeys);
  }

  function removeProjectChangedState(key: number) {
    let newStates = allProjectsChangedState.slice(0, key).concat(allProjectsChangedState.slice(key + 1));
    setAllProjectsChangedState(newStates);
    let newKeys = allProjectsArbitraryKeys.slice(0, key).concat(allProjectsArbitraryKeys.slice(key + 1));
    setAllProjectsArbitraryKeys(newKeys);
  }

  const { updateUserProject } = useProfile()
  const [error, setError] = useState('')

  useEffect(() => setDirtyForm(isDirty), [isDirty])
  useEffect(() => {
    getPracticeAreaList(setPracticeAreaListData)
    getIndustryList(setIndustryListData)
    getSoftwareProductList(setSoftwareProductListData)
    getLanguageList(setLanguageListData)
    getResponsibilityList(setResponsibilityListData)
    getMethodologyList(setMethodologiesListData)
    setAllProjectsChangedState(Array.from({ length: fields.length }, () => false))
    let newKeys = new Uint32Array(fields.length);
    crypto.getRandomValues(newKeys);
    setAllProjectsArbitraryKeys(Array.from(newKeys));
  }, [])

  const updateProfile: SubmitHandler<IDataToSubmit> = (data) => {
    setDirtyForm(false)

    // map the ids from fields to the projects
    let projects = fields.map((field, index) => ({
      ...data.projects[index],
      id: field.id || data.projects[index].id
    }))
    projects = projects.map((project) => {
      delete project.practiceArea
      delete project.industry
      delete project.softwareProduct
      delete project.reviewMethodology
      delete project.languages
      project.durationMonths = project.durationMonths ? project.durationMonths.toString() : '0'
      let isNumberBySimpleCheck = /^[0-9]+$/.test(project.durationMonths.trim());
      if (!isNumberBySimpleCheck || parseFloat(project.durationMonths) < 0){
        project.durationMonths = "0";
      }
      return project
    })

    let updatedProfileData: IProjectData = {
      attributes: {
        projects: projects,
        noPriorExperience: projects.length > 0 ? false : data?.noPriorExperience
      },
      setProfileData: setProfileData,
      setError: setError
    }

    console.log(updatedProfileData)
    console.log('data', data)

    updateUserProject(updatedProfileData)
    setAllProjectsToOneChangedState(false)
  }

  const AddProjectHandler = () => {
    const newProject: UserProject = {
      id: null,
      isValid: false,
      durationMonths: 0,
      responsibilities: [],
      reviewMethodology: { name: "", value: "" },
      practiceArea: { name: "", value: "" },
      industry: { name: "", value: "" },
      languages: [languageListData?.languageList?.find(lang => lang.name === 'English') as DropDownOption], // Set default language to English
      softwareProduct: softwareProductListData?.softwareProductList?.find(lang => lang.name === 'Relativity') as DropDownOption
    }

    append(newProject)
    addNewProjectChangedState(true)
  }

  const onCloseHandler = (id: number) => {
    remove(id);
    removeProjectChangedState(id);
  }

  return (
    <div className={classes.section}>
      <form className={classes.form} onSubmit={handleSubmit(updateProfile)} >
        <InformationSection title="Document Review Projects">
          <label className={classes.label}>
            Reviewers who include details on at least five document review projects are four times more likely to be placed. Most reviewers include information on their top 10 projects or more (in terms of length or number of project responsibilities).
          </label>
          {
            fields && fields.map((project, index) => {
              return (
                <Project
                  key={allProjectsArbitraryKeys[index]}
                  index={index}
                  register={register}
                  practiceAreaListData={practiceAreaListData}
                  industryListData={industryListData}
                  softwareProductListData={softwareProductListData}
                  responsibilityListData={responsibilityListData}
                  methodologiesListData={methodologiesListData}
                  languageListData={languageListData}
                  project={project}
                  expand={(project.industry && project.industry.name) === '' && (project.practiceArea && project.practiceArea.name) === '' ? true : false}
                  onCloseHandler={() => onCloseHandler(index)}
                  hasChanged={() => projectHasChanged(index)}
                  setHasChanged={() => setProjectHasChanged(index, true)}
                />
              )
            })
          }

          <div className={classes.add_button}>
            <Button type='button' size='small' privacy='allow' onClickMethod={AddProjectHandler} innerText='+ Add Project' variant='outlined' />
          </div>
        </InformationSection>

        <div className={classes.checkbox_field} hidden={fields.length > 0}>
          <input
            type="checkbox"
            defaultChecked={profileData?.noPriorExperience}
            data-dd-privacy="allow"
            {...register('noPriorExperience')}
          />
          <span>No prior document review experience.</span>
        </div>

        {error && <div className='text-danger'>{error}</div>}
        <div className={classes.submit_button}>
          <Button type='submit' size='large' innerText='Update Profile' />
        </div>
      </form>
    </div>
  )
}

export default Projects
